import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileDown, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, inr } from "@/lib/api";
import { download } from "@/components/gst";

const HEADS = ["igst", "cgst", "sgst", "cess"];

function Cell({ path, auto, value, onChange, disabled }) {
  const edited = value !== undefined && Number(value) !== auto;
  return (
    <td className="border px-1 py-1">
      <input type="number" step="0.01" min="0" disabled={disabled} data-testid={`g3b-${path}`}
        className={`w-full min-w-[90px] rounded bg-transparent px-1 text-right font-mono text-xs outline-none focus:ring-1 focus:ring-primary ${edited ? "bg-amber-500/15 font-bold" : ""}`}
        value={value ?? auto} onChange={(e) => onChange(path, e.target.value)} title={edited ? `Auto: ${auto}` : "Auto-filled"} />
    </td>
  );
}

function Grid({ tbl, rows, fields, d, ov, setOv, locked, extra }) {
  const onChange = (path, v) => setOv((o) => ({ ...o, [path]: v }));
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full text-sm" data-testid={`g3b-table-${tbl}`}>
        <thead><tr className="bg-secondary/60 text-xs">{["Details", ...fields.map((f) => (f === "taxable" ? "Taxable Value" : f.toUpperCase()))].map((h) => <th key={h} className="border px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((k) => (
            <tr key={k}><td className="border px-2 py-1 text-xs">{d.labels[tbl][k]}</td>
              {fields.map((f) => <Cell key={f} path={`${tbl}.${k}.${f}`} auto={d.auto[tbl][k][f]} value={ov[`${tbl}.${k}.${f}`]} onChange={onChange} disabled={locked} />)}</tr>
          ))}
          {extra}
        </tbody>
      </table>
    </div>
  );
}

function Payment({ d }) {
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full text-sm" data-testid="g3b-payment">
        <thead><tr className="bg-secondary/60 text-xs">{["Head", "Liability (3.1 a+b)", "Paid via IGST ITC", "Paid via CGST ITC", "Paid via SGST ITC", "Paid via Cess ITC", "RCM 3.1(d) (cash only)", "Total Cash Payable", "ITC Carry Forward"].map((h) => <th key={h} className="border px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
        <tbody>
          {Object.entries(d.payment).map(([h, x]) => (
            <tr key={h} data-testid={`g3b-pay-${h}`}><td className="border px-2 py-1 font-semibold uppercase">{h}</td>
              {["liability", "itc_igst", "itc_cgst", "itc_sgst", "itc_cess", "rcm_cash", "cash", "carry_forward"].map((k) => <td key={k} className={`amount border px-2 py-1 text-right ${k === "cash" ? "font-bold text-rose-600" : ""}`}>{inr(x[k], 2)}</td>)}</tr>
          ))}
          <tr className="font-bold"><td className="border px-2 py-1" colSpan={7}>Total Cash Payable (GST)</td><td className="amount border px-2 py-1 text-right text-rose-600" data-testid="g3b-cash-total">{inr(d.cash_total, 2)}</td><td className="border" /></tr>
        </tbody>
      </table>
    </div>
  );
}

export function Gstr3bTab({ month }) {
  const [d, setD] = useState(null);
  const [ov, setOv] = useState({});
  const [err, setErr] = useState(false);
  const cur = useRef(month);
  const load = () => {
    const m = month;
    setErr(false);
    return api.get("/gst/gstr3b", { params: { month: m } }).then((r) => { if (cur.current === m) { setD(r.data); setOv(r.data.overrides || {}); } })
      .catch((e) => { if (cur.current === m) { setErr(true); toast.error(errMsg(e)); } });
  };
  useEffect(() => { cur.current = month; setD(null); load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const put = async (overrides, msg) => {
    try { const { data } = await api.put("/gst/gstr3b", { overrides }, { params: { month } }); setD(data); setOv(data.overrides || {}); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  if (err) return <div className="space-y-2 text-sm" data-testid="g3b-error">GSTR-3B draft load nahi hua. <Button size="sm" variant="outline" data-testid="g3b-retry-btn" onClick={load}>Retry</Button> <Button size="sm" variant="outline" data-testid="g3b-error-reset-btn" onClick={() => put({}, "Reset to auto figures")}>Reset to auto</Button></div>;
  if (!d || d.month !== month) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const changed = Object.fromEntries(Object.entries(ov).filter(([k, v]) => { const [t, r, f] = k.split("."); return v !== "" && Number(v) !== d.auto[t][r][f]; }));
  const netRow = <tr className="font-bold"><td className="border px-2 py-1 text-xs">4C Net ITC available (A − B)</td>{HEADS.map((h) => <td key={h} className="amount border px-2 py-1 text-right text-xs" data-testid={`g3b-net-${h}`}>{inr(d.net_itc[h], 2)}</td>)}</tr>;
  const saved = d.overrides || {};
  const dirty = Object.keys({ ...changed, ...saved }).some((k) => !(k in changed) || !(k in saved) || Number(changed[k]) !== Number(saved[k]));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3" data-testid="g3b-summary-chip">
        <span className="text-sm">Total Cash Payable: <b className="amount text-rose-600" data-testid="g3b-cash-chip">{inr(d.cash_total, 2)}</b></span>
        {dirty && <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400" data-testid="g3b-unsaved">Unsaved changes — Save dabayein, phir Net ITC aur payment update honge</span>}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">Auto-filled from GSTR-1 & GSTR-2B. Yellow cells = manually edited.{d.updated_by ? ` Last saved by ${d.updated_by}.` : ""}{d.locked ? " Period locked — editing disabled." : ""}</div>
        <div className="flex flex-wrap gap-2">
          {!d.locked && <Button size="sm" data-testid="g3b-save-btn" onClick={() => put(changed, "GSTR-3B draft saved")}><Save className="mr-1 h-4 w-4" />Save Draft</Button>}
          {!d.locked && <Button size="sm" variant="outline" data-testid="g3b-reset-btn" onClick={() => put({}, "Reset to auto figures")}><RotateCcw className="mr-1 h-4 w-4" />Reset to auto</Button>}
          <Button size="sm" variant="outline" data-testid="g3b-excel-btn" onClick={() => download("/gst/gstr3b/excel", { month }, `GSTR3B_Draft_${month}.xlsx`)}><FileSpreadsheet className="mr-1 h-4 w-4" />Excel</Button>
          <Button size="sm" variant="outline" data-testid="g3b-pdf-btn" onClick={() => download("/gst/gstr3b/pdf", { month }, `GSTR3B_Draft_${month}.pdf`)}><FileDown className="mr-1 h-4 w-4" />PDF</Button>
        </div>
      </div>
      <h3 className="font-semibold">3.1 Outward supplies & inward supplies liable to reverse charge</h3>
      <Grid tbl="t31" rows={["a", "b", "c", "d", "e"]} fields={["taxable", ...HEADS]} d={d} ov={ov} setOv={setOv} locked={d.locked} />
      <p className="text-xs text-muted-foreground" data-testid="g3b-rcm-note">RCM wali outward GTA supplies (taxable {inr(d.rcm_outward_taxable, 2)}) sirf GSTR-1 me jaati hain — tax party bharti hai, isliye 3.1 me nahi.</p>
      <h3 className="font-semibold">4. Eligible ITC</h3>
      <Grid tbl="t4" rows={["a1", "a2", "a3", "a4", "a5", "b1", "b2", "d1", "d2"]} fields={HEADS} d={d} ov={ov} setOv={setOv} locked={d.locked} extra={netRow} />
      <p className="text-xs text-muted-foreground">4A(5) me sirf GSTR-2B me matched ITC (rule 36(4)); 17(5) blocked bills 4B(1) me reverse hote hain.</p>
      <h3 className="font-semibold">Tax Payment (ITC set-off ke baad)</h3>
      <Payment d={d} />
      <p className="text-xs text-muted-foreground">Set-off order (rule 88A): IGST credit pehle IGST, phir CGST/SGST me (cash kam se kam rahe is tarah); uske baad CGST → CGST, IGST; SGST → SGST, IGST. Edit karke Save dabayein — final figures CA se confirm karein.</p>
    </div>
  );
}
